import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import {
  closeIcon,
  daggerMark,
  daggerText,
  menuIcon,
  searchIcon,
} from "../../icons"
import "./header.component.css"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { daggerNavigate } from "../../utils/navigate.util"
import SiteMenuComponent from "../site-menu/site-menu.component"

const HeaderComponent = (props) => {
  const { HexColor } = props
  const [showMenu, setShowMenu] = useState()
  const [items, setItems] = useState({})
  const elRef = useRef()
  const hexRef = useRef()
  const fullMenuRef = useRef()
  const { pathname } = useLocation()

  const normalizedPath = pathname.toLowerCase().replace(/\/+$/, "").trim()
  const isHomeRoute = ["", "/", "/home"].includes(normalizedPath)

  // Close the expanded menu on route change
  useEffect(() => {
    if (fullMenuRef.current) {
      fullMenuRef.current.classList.remove("active")
    }
    setShowMenu(false)
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute("content", "#000000")
  }, [pathname])

  // Get items on header event
  useEffect(() => {
    const loadItems = () => {
      const store = window.sessionStorage.getItem("__dagger-store__")
      if (store) {
        const json = JSON.parse(store)
        setItems({ ...json.header, ...json.settings })
      }
    }

    document.addEventListener("EVENTS.HEADER_LOADED", loadItems)
    loadItems()
    return () => document.removeEventListener("EVENTS.HEADER_LOADED", loadItems)
  }, [])

  // Set hex color
  useEffect(() => {
    if (HexColor) {
      hexRef.current = HexColor
    } else {
      hexRef.current = null
    }
  }, [HexColor])

  // Setup scrolltrigger to update background on scroll
  useLayoutEffect(() => {
    const handleScrollDown = () => {
      if (elRef.current) {
        elRef.current.classList.add("activated")

        if (hexRef.current && elRef.current?.style) {
          elRef.current.style.backgroundColor = hexRef.current
        }
      }
    }

    const handleScrollUp = () => {
      if (elRef.current) {
        elRef.current.classList.remove("activated")

        if (hexRef.current && elRef.current?.style) {
          elRef.current.style.backgroundColor = "initial"
        }
      }
    }

    const trigger = ScrollTrigger.create({
      trigger: `.dagger-header`,
      start: "10% top",
      onEnter: handleScrollDown,
      onLeaveBack: handleScrollUp,
    })

    return () => trigger.kill()
  }, [])

  const handleCloseMenu = useCallback(() => {
    if (fullMenuRef.current) {
      fullMenuRef.current.classList.remove("active")
    }

    setThemeColor("#000000")

    setTimeout(() => {
      setShowMenu(false)
    }, 250)
  }, [])

  // Dynamically detect background color behind the header
  useLayoutEffect(() => {
    let rafId
    const el = elRef.current

    const isLightColor = (color) => {
      const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
      if (!match) return false
      const r = Number(match[1])
      const g = Number(match[2])
      const b = Number(match[3])
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.5
    }

    const getBackgroundColor = (element) => {
      let target = element
      while (target && target !== document.documentElement) {
        const bg = window.getComputedStyle(target).backgroundColor
        if (bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)") {
          return bg
        }
        target = target.parentElement
      }
      return null
    }

    const checkBackground = () => {
      if (!elRef.current) return

      const headerRect = elRef.current.getBoundingClientRect()
      const checkY = headerRect.height / 2
      const checkX = window.innerWidth / 2

      // Hide header from hit testing to get the element behind it
      elRef.current.style.pointerEvents = "none"
      const behindEl = document.elementFromPoint(checkX, checkY)
      elRef.current.style.pointerEvents = ""

      if (!behindEl) return

      const bgColor = getBackgroundColor(behindEl)

      if (bgColor && isLightColor(bgColor)) {
        elRef.current.classList.add("inverted")
      } else {
        elRef.current.classList.remove("inverted")
      }
    }

    const onScroll = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(checkBackground)
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    const timer = setTimeout(checkBackground, 500)

    return () => {
      window.removeEventListener("scroll", onScroll)
      cancelAnimationFrame(rafId)
      clearTimeout(timer)
      if (el) {
        el.classList.remove("inverted")
      }
    }
  }, [pathname])

  // Listen for invert event
  useEffect(() => {
    const handleInvert = (e) => {
      if (!elRef.current) return

      const invert = e.detail
      if (invert) {
        elRef.current.classList.add("inverted")
      } else {
        elRef.current.classList.remove("inverted")
      }
    }

    document.addEventListener("EVENTS.INVERT_HEADER", handleInvert)
    document.addEventListener("EVENTS.CLOSE_HEADER", handleCloseMenu)

    return () => {
      document.removeEventListener("EVENTS.INVERT_HEADER", handleInvert)
      document.removeEventListener("EVENTS.CLOSE_HEADER", handleCloseMenu)
    }
  }, [handleCloseMenu])

  const setThemeColor = (color) => {
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute("content", color)
  }

  const handleShowMenu = () => {
    setShowMenu(true)
    setThemeColor("#0000ff")
    setTimeout(() => {
      fullMenuRef.current.classList.add("active")
    })
  }

  const openContactForm = (defaultSubject) => {
    document.dispatchEvent(
      new CustomEvent("EVENTS.OPEN_CONTACT_FORM", {
        detail: { defaultSubject },
      }),
    )
  }

  const navigate = useNavigate()

  const handleShowLoader = (e, dest) => {
    if (dest === window.location.pathname) {
      e.preventDefault()
      handleCloseMenu()
      return
    }
    e.preventDefault()
    daggerNavigate(() => navigate(dest))
  }

  return (
    <header
      className={`dagger-header ${isHomeRoute ? "is-home" : "is-not-home"}`}
      ref={elRef}
    >
      <Link
        to={`/`}
        onClick={(e) => handleShowLoader(e, `/`)}
        className="dagger-top-level dagger-main-mark"
      >
        {daggerMark}
      </Link>
      <button
        className="dagger-top-level search-btn"
        onClick={() =>
          document.dispatchEvent(new CustomEvent("EVENTS.OPEN_SPOTLIGHT"))
        }
        aria-label="Search"
      >
        {searchIcon}
      </button>
      <button
        className="dagger-top-level menu-btn"
        onClick={handleShowMenu}
        aria-label="Menu"
      >
        {menuIcon}
      </button>
      {showMenu && (
        <div className="dagger-header-menu">
          <button
            className="dagger-header-menu-close-btn"
            onClick={handleCloseMenu}
            aria-label="Close menu"
          >
            {closeIcon}
          </button>
          <div className="dagger-header-menu-content" ref={fullMenuRef}>
            <div className="dagger-header-menu-content-top">
              <SiteMenuComponent items={items} />
              <div className="dagger-header-menu-content-top-extras">
                <h2>{items.ContactHeading}</h2>
                <p>
                  {items.ContactAddressLineOne}
                  <br />
                  {items.ContactAddressLineTwo}
                  <br />
                  {items.ContactAddressLineThree}
                </p>
                {items.ContactPhone && (
                  <a href={`tel:+1${items.ContactPhone.replaceAll(".", "")}`}>
                    {items.ContactPhone}
                  </a>
                )}
                <a href={`mailto:${items.ContactEmail}`}>
                  {items.ContactEmail}
                </a>
                <a
                  className="underline"
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault()
                    openContactForm("General Inquiry")
                  }}
                >
                  Contact
                </a>
                <div className="dagger-header-menu-content-top-extras-bottom">
                  {/* <a
                    href="#contact"
                    onClick={(e) => {
                      e.preventDefault()
                      openContactForm("Press")
                    }}
                  >
                    Press
                  </a> */}
                </div>
              </div>
            </div>
            <div className="dagger-header-menu-wordmark">{daggerText}</div>
          </div>
        </div>
      )}
    </header>
  )
}

export default HeaderComponent
